import { action } from 'mobx';
import { observer } from 'mobx-react';
import { bind, HelpLink, IMultiSelectOption, InfiniteScroll, Loader, NothingHere } from 'Mri.Web.Common';
import * as React from 'react';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { Input } from 'reactstrap';
import { ColumnSelector } from '../DisplayFilters/ColumnSelector';
import { DisplayFilter } from '../DisplayFilters/DisplayFilter';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { DisplayHeader } from '../Displays/DisplayHeader';
import { DisplayRow } from '../Displays/DisplayRow';

export interface IManageDisplayListCardProps {
    action?(event: React.MouseEvent<HTMLButtonElement>): void;
    readonly vm: DisplayListViewModel;
    readonly history?: any;
    readonly title: string;
    readonly actionTitle?: string;
    readonly actionMessage?: string;
    readonly runningAction?: boolean;
    readonly renderDisplayFilter?: boolean;
    readonly height: number;
}

@observer
export class ManageDisplayListCard extends React.Component<IManageDisplayListCardProps> {
    public constructor(props: IManageDisplayListCardProps) {
        super(props);

        this.columnDefaults = props.vm.columns.slice();
    }

    public render() {
        const { title, actionTitle, actionMessage, runningAction, renderDisplayFilter, height, vm } = this.props;

        const {
            columns,
            hasMore,
            busy,
            loadMoreAsync,
            total,
            scrollCount,
            onColumnChange,
            onFilterDropdownToggle,
            displays,
            checkedDisplays,
            selectAllColumnsChecked,
        } = this.props.vm;

        return (
            <div className="card">
                <div className="card-header bg-btn-primary flex-items-center py-2">
                    {title} {total > 0 ? total : 0}
                    <HelpLink className="ms-auto" url="https://support.mri-inc.net/hc/en-us/articles/360003511032" />
                    {this.props.action && actionMessage && runningAction != undefined && (
                        <button
                            className="btn btn-secondary"
                            disabled={checkedDisplays.length <= 0}
                            onClick={this.props.action}
                        >
                            <Loader message={actionMessage} showSpinner loading={runningAction} />
                            {!runningAction && `${actionTitle} ${checkedDisplays.length} Selected`}
                        </button>
                    )}
                </div>
                <div className="card-body p-0">
                    {renderDisplayFilter && (
                        <DisplayFilter
                            vm={this.props.vm}
                            inputClass="border-0"
                            renderAfter={
                                <div className="ms-auto flex-items-center">
                                    <div className="extra-filters show">
                                        <ColumnSelector
                                            columns={columns}
                                            onToggle={onFilterDropdownToggle}
                                            onChange={onColumnChange}
                                            onReset={this.onColumnReset}
                                            className="d-inline-block ms-1"
                                            selectAllColumnsChecked={selectAllColumnsChecked}
                                            onColumnGroupSelectAllChange={(group, checked) => {
                                                const x = 0;
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    )}

                    <div style={{ overflowY: 'scroll', height }} ref={this.setContainer}>
                        <InfiniteScroll
                            throttle={100}
                            threshold={300}
                            hasMore={hasMore}
                            isLoading={busy}
                            container={this.container}
                            onLoadMore={loadMoreAsync}
                        >
                            <table className="table table-striped">
                                <DisplayHeader renderBefore={this.renderHeaderCheckbox} vm={vm} />
                                <tbody>
                                    {displays.slice(0, scrollCount).map(d => (
                                        <DisplayRow
                                            key={d.serialNumber}
                                            display={d}
                                            renderBefore={this.renderCheckbox}
                                            history={this.props.history}
                                            vm={vm}
                                        />
                                    ))}
                                </tbody>
                            </table>
                            <NothingHere
                                empty={!displays.length}
                                forceLoaderOnLoad
                                icon={<BsTv />}
                                loading={busy}
                                label="No Displays Returned From Search"
                                loadingMessage="Loading Displays..."
                            />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    private setContainer(r: HTMLDivElement | null) {
        this.container = r;
    }

    @bind
    private renderCheckbox(display: ActiveDisplay) {
        const { checkedDisplays, toggleCheck } = this.props.vm;

        return (
            <div>
                <Input
                    type="checkbox"
                    checked={checkedDisplays.includes(display)}
                    onChange={() => toggleCheck(display)}
                    className="ms-4"
                />{' '}
            </div>
        );
    }

    @bind
    private renderHeaderCheckbox() {
        const { allChecked, toggleCheckAllAsync } = this.props.vm;

        return (
            <th className="p-2">
                <Input type="checkbox" checked={allChecked} onChange={toggleCheckAllAsync} className="ms-4" />{' '}
                {this.props.actionTitle}
            </th>
        );
    }

    @bind
    @action
    private onColumnReset() {
        this.props.vm.onColumnChange(this.columnDefaults);
    }

    private container: HTMLDivElement | null = null;
    private columnDefaults: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
}
